import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, verifyEmail } from '../api.js';
import { LOGIN_PATH } from '../lib/auth.js';
import Spinner from '../components/Spinner.js';
import GirisKabugu, {
  AnimateIn,
  KartAciklama,
  KartBaslik,
  MarkaButonu,
  UST_HAP,
} from '../components/GirisKabugu.js';

/**
 * §43.2 — `/dogrula?token=…`: doğrulama mektubundaki linkin hedefi.
 *
 * GÖRSEL DİL `GirisKabugu` ile referans admin paneline taşındı; DAVRANIŞ, tüm
 * `data-testid`ler ve metinler önceki sürümle birebir aynıdır.
 *
 * Sayfa route guard'ın DIŞINDADIR: doğrulanmamış kullanıcı §43.2 gereği giriş
 * YAPAMAZ, yani buraya gelen kişinin tanımı gereği oturumu yoktur.
 *
 * Doğrulama OTURUM AÇMAZ (davet kabulünden farkı budur): sunucu yalnız
 * `email_verified_at` yazar. Bu yüzden başarıda tek gidilecek yer `/giris`'tir ve
 * yönlendirme `replace` ile yapılır — kullanıcı geri tuşuyla TÜKETİLMİŞ tokenlı
 * adrese dönüp "bağlantı geçersiz" hatası görmemelidir.
 */

/** Hata kartlarının ortak gövdesi — başlık + açıklama + girişe dönüş. */
function VerifyError({
  testId,
  title,
  description,
}: {
  testId: string;
  title: string;
  description: string;
}) {
  return (
    <div data-testid={testId} className="flex flex-col gap-6">
      <AnimateIn delay={0}>
        <KartBaslik>{title}</KartBaslik>
      </AnimateIn>
      <AnimateIn delay={60} className="flex flex-col gap-4">
        <KartAciklama>{description}</KartAciklama>
        <Link to={LOGIN_PATH} className="w-full">
          <MarkaButonu type="button">Girişe dön</MarkaButonu>
        </Link>
      </AnimateIn>
    </div>
  );
}

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // §43.2 — link biçimi `?token=<uuid>`; token yoksa doğrulanacak bir kayıt da yoktur.
  const token = params.get('token') ?? '';

  const verify = useMutation({
    mutationFn: () => verifyEmail(token),
    onSuccess: () => {
      /*
       * `state.verified` giriş kartında tek satırlık olumlu bir not olur: kullanıcı
       * mektuptan gelip aniden giriş formuyla karşılaşınca "doğrulama oldu mu?"
       * sorusunu sormamalıdır.
       */
      navigate(LOGIN_PATH, { replace: true, state: { verified: true } });
    },
  });

  /*
   * Doğrulama MOUNT'TA kendiliğinden koşar: mektuptaki linke tıklayan kişiden ayrıca
   * bir düğmeye basmasını istemek, akışa hiçbir bilgi katmayan fazladan bir adımdır.
   *
   * `ref` kilidi StrictMode içindir: geliştirmede efekt İKİ KEZ koşar ve token TEK
   * KULLANIMLIKTIR — ikinci çağrı 400 döner ve başarılı bir doğrulama "geçersiz
   * bağlantı" gibi görünürdü.
   */
  const started = useRef(false);
  const verifyRef = useRef(verify);
  verifyRef.current = verify;
  useEffect(() => {
    if (token === '' || started.current) return;
    started.current = true;
    verifyRef.current.mutate();
  }, [token]);

  const ustSag = (
    <Link to={LOGIN_PATH} className={UST_HAP}>
      Girişe dön
    </Link>
  );

  if (token === '') {
    return (
      <GirisKabugu testId="dogrula-sayfasi" duzen="ortala" ustSag={ustSag}>
        <VerifyError
          testId="dogrula-baglanti-eksik"
          title="Bağlantı eksik"
          description="Doğrulama bağlantısı token içermiyor. E-postadaki adresi tarayıcıya olduğu gibi yapıştırdığınızdan emin olun."
        />
      </GirisKabugu>
    );
  }

  const status = verify.error instanceof ApiError ? verify.error.status : undefined;

  // §43.1 — bayrak kapalıysa uç 404'tür; "geçersiz token" demek yanıltıcı olurdu.
  if (status === 404) {
    return (
      <GirisKabugu testId="dogrula-sayfasi" duzen="ortala" ustSag={ustSag}>
        <VerifyError
          testId="dogrula-kapali"
          title="Doğrulama kapalı"
          description="Bu kurulumda self-serve kayıt açık değil. Hesabınız davetle açıldıysa doğrulamaya gerek yoktur; doğrudan giriş yapın."
        />
      </GirisKabugu>
    );
  }

  if (verify.isError) {
    /*
     * Kullanılmış, süresi geçmiş ve hiç var olmamış token sunucuda AYNI 400'ü alır
     * (§43.2) — panel de bu üçünü ayırmaz, çünkü ayıramaz. Çözüm yolu tektir: giriş
     * denemesi, doğrulanmamış hesapta "yeniden gönder" düğmesini açar.
     */
    return (
      <GirisKabugu testId="dogrula-sayfasi" duzen="ortala" ustSag={ustSag}>
        <VerifyError
          testId="dogrula-gecersiz"
          title="Bağlantı geçersiz"
          description="Bu doğrulama bağlantısı kullanılmış ya da süresi dolmuş olabilir. Girişte hesabınızla devam edin: doğrulanmamışsa yeni bir mektup gönderebilirsiniz."
        />
      </GirisKabugu>
    );
  }

  /*
   * Başarı: `onSuccess` zaten `/giris`'e yönlendirir; bu kart yalnız yönlendirme
   * gerçekleşene kadarki kareyi doldurur. "Doğrulanıyor" ile "doğrulandı" ayrı
   * yazılır — ikisini tek metinle geçmek, hata durumunda yalan söylerdi.
   */
  return (
    <GirisKabugu testId="dogrula-sayfasi" duzen="ortala" ustSag={ustSag}>
      <div data-testid="dogrula-durum" className="flex flex-col gap-6">
        {verify.isSuccess ? (
          <>
            <AnimateIn delay={0}>
              <KartBaslik>E-posta doğrulandı</KartBaslik>
            </AnimateIn>
            <AnimateIn delay={60}>
              <KartAciklama>Giriş sayfasına yönlendiriliyorsunuz…</KartAciklama>
            </AnimateIn>
          </>
        ) : (
          <AnimateIn delay={0} className="flex flex-col items-center gap-3 py-4 text-center">
            <Spinner size="lg" label="Doğrulanıyor…" />
            <KartAciklama>E-posta adresiniz doğrulanıyor, bu birkaç saniye sürebilir.</KartAciklama>
          </AnimateIn>
        )}
      </div>
    </GirisKabugu>
  );
}
